import React, { useState, useMemo } from 'react';
import { Button } from '../ui/button';
import { Plus, Trash2, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';
import { weddingBudgetCategories } from '../../data/templateCustomizationSchemas';

const defaultItem = { name: '', estimated: '', actual: '', deposit: '', depositPaid: false, paid: '', deadline: '', vendor: '', notes: '' };

const formatCurrency = (val) => {
  const num = parseFloat(val);
  return isNaN(num) ? 0 : num;
};

const BudgetCategory = ({ category, items, onUpdateItems, expanded, onToggle, guestCount }) => {
  const addItem = () => onUpdateItems([...items, { ...defaultItem }]);
  const removeItem = (idx) => onUpdateItems(items.filter((_, i) => i !== idx));
  const updateItem = (idx, field, value) => {
    const updated = [...items];
    updated[idx] = { ...updated[idx], [field]: value };
    onUpdateItems(updated);
  };

  const totals = useMemo(() => ({
    estimated: items.reduce((s, i) => s + formatCurrency(i.estimated), 0),
    actual: items.reduce((s, i) => s + formatCurrency(i.actual), 0),
    deposit: items.reduce((s, i) => s + (i.depositPaid ? formatCurrency(i.deposit) : 0), 0),
    paid: items.reduce((s, i) => s + formatCurrency(i.paid), 0),
    remaining: items.reduce((s, i) => s + formatCurrency(i.actual || i.estimated) - formatCurrency(i.paid), 0),
  }), [items]);

  return (
    <div className={`border rounded-xl transition-all ${expanded ? 'border-primary/30 bg-white' : 'border-stone-200'}`}>
      <div className="flex items-center gap-3 p-3 cursor-pointer" onClick={onToggle}>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-sans font-semibold text-stone-800">{category.name}</p>
          <p className="text-xs font-sans text-stone-400">{items.length} poster</p>
        </div>
        <div className="text-right mr-2">
          <p className="text-sm font-sans font-bold text-stone-700">{totals.estimated.toLocaleString('en-US')} kr</p>
          {totals.paid > 0 && <p className="text-[10px] font-sans text-green-600">{totals.paid.toLocaleString('en-US')} kr paid</p>}
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-stone-400" /> : <ChevronDown className="w-4 h-4 text-stone-400" />}
      </div>

      {expanded && (
        <div className="px-3 pb-3 border-t border-stone-100 space-y-2 pt-2">
          {items.map((item, idx) => (
            <div key={idx} className="bg-stone-50/80 rounded-lg p-2.5 space-y-2">
              <div className="flex items-center gap-2">
                <input value={item.name} onChange={e => updateItem(idx, 'name', e.target.value)} placeholder="Kostnadsnavn"
                  className="flex-1 text-xs font-sans px-2 py-1.5 border border-stone-200 rounded-lg bg-white focus:border-primary focus:outline-none" />
                <button onClick={() => removeItem(idx)} className="text-stone-400 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5">
                <NumField label="Estimert" value={item.estimated} onChange={v => updateItem(idx, 'estimated', v)} />
                <NumField label="Faktisk" value={item.actual} onChange={v => updateItem(idx, 'actual', v)} />
                <NumField label="Depositum" value={item.deposit} onChange={v => updateItem(idx, 'deposit', v)} />
                <NumField label="Paid" value={item.paid} onChange={v => updateItem(idx, 'paid', v)} />
              </div>
              <div className="grid grid-cols-2 gap-1.5">
                <input value={item.deadline} onChange={e => updateItem(idx, 'deadline', e.target.value)} type="date"
                  className="text-[10px] font-sans px-2 py-1 border border-stone-200 rounded-lg bg-white focus:border-primary focus:outline-none" />
                <input value={item.vendor} onChange={e => updateItem(idx, 'vendor', e.target.value)} placeholder="Vendor"
                  className="text-[10px] font-sans px-2 py-1 border border-stone-200 rounded-lg bg-white focus:border-primary focus:outline-none" />
              </div>
            </div>
          ))}
          <Button type="button" onClick={addItem} variant="ghost" size="sm" className="text-xs font-sans text-stone-500 gap-1">
            <Plus className="w-3 h-3" /> Add item
          </Button>
          {guestCount > 0 && totals.estimated > 0 && (
            <p className="text-[10px] font-sans text-stone-400 pt-1">
              ≈ {Math.round(totals.estimated / guestCount).toLocaleString('en-US')} kr per guest
            </p>
          )}
        </div>
      )}
    </div>
  );
};

const NumField = ({ label, value, onChange }) => (
  <div>
    <label className="text-[9px] font-sans text-stone-400 uppercase">{label}</label>
    <input type="number" value={value} onChange={e => onChange(e.target.value)} placeholder="0"
      className="w-full text-[11px] font-sans px-2 py-1 border border-stone-200 rounded-lg bg-white focus:border-primary focus:outline-none" />
  </div>
);

export const BudgetTracker = ({ budget, onChange, guestCount = 0, templateData }) => {
  const [expandedCat, setExpandedCat] = useState(null);
  const totalBudget = parseFloat(budget.totalBudget) || 0;

  const updateCategory = (catId, items) => {
    onChange({ ...budget, categories: { ...budget.categories, [catId]: items } });
  };

  const bufferPercent = parseFloat(budget.bufferPercent) || 0;
  const bufferAmount = totalBudget > 0 ? Math.round(totalBudget * (bufferPercent / 100)) : (parseFloat(budget.bufferAmount) || 0);

  const masterTotals = useMemo(() => {
    let estimated = 0, actual = 0, paid = 0, depositPaid = 0;
    let upcomingDeadlines = [];
    const now = new Date();
    const twoWeeks = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000);
    Object.entries(budget.categories || {}).forEach(([catId, items]) => {
      items.forEach(item => {
        estimated += formatCurrency(item.estimated);
        actual += formatCurrency(item.actual);
        paid += formatCurrency(item.paid);
        if (item.depositPaid) depositPaid += formatCurrency(item.deposit);
        if (item.deadline && !item.paid) {
          const dl = new Date(item.deadline);
          if (dl >= now && dl <= twoWeeks) {
            upcomingDeadlines.push({ name: item.name || catId, deadline: item.deadline, amount: formatCurrency(item.estimated) - formatCurrency(item.paid) });
          }
        }
      });
    });
    return { estimated, actual, paid, depositPaid, remaining: (actual || estimated) - paid, upcomingDeadlines };
  }, [budget.categories]);

  // Cross-module cost aggregation
  const crossModuleeCosts = useMemo(() => {
    const costs = [];
    // Kids activities
    const kidsActivityCost = (templateData?.kids?.activities || []).reduce((s, a) => s + (parseFloat(a.price) || 0), 0);
    if (kidsActivityCost > 0) costs.push({ label: 'Childeaktiviteter', amount: kidsActivityCost });
    // Drinks
    const drinkCost = Object.values(templateData?.food?.drinks || {}).reduce((s, d) => s + ((parseFloat(d.quantity) || 0) * (parseFloat(d.pricePerUnit) || 0)), 0);
    if (drinkCost > 0) costs.push({ label: 'Drikke (detaljert)', amount: drinkCost });
    // Transport
    const transportCost = (templateData?.transport?.rides || []).reduce((s, r) => s + (parseFloat(r.price) || 0), 0);
    if (transportCost > 0) costs.push({ label: 'Transport', amount: transportCost });
    // Accommodation
    const accomCost = (templateData?.transport?.accommodations || []).reduce((s, a) => s + (parseFloat(a.price) || 0) * (parseInt(a.rooms) || 1), 0);
    if (accomCost > 0) costs.push({ label: 'Accommodation', amount: accomCost });
    return costs;
  }, [templateData]);

  const overBudget = totalBudget > 0 && masterTotals.estimated > (totalBudget - bufferAmount);

  // Hidden cost reminders
  const hiddenCosts = useMemo(() => {
    const reminders = [];
    if (!(budget.categories?.tips || []).length) reminders.push('Vendor tips');
    if (!(budget.categories?.insurance || []).length) reminders.push('Wedding insurance');
    if (!(budget.categories?.alterations || []).length && templateData?.attire) reminders.push('Attire alterations/adjustments');
    if (!(budget.categories?.transport || []).length && !templateData?.transport?.rides?.length) reminders.push('Transport (couple, guests)');
    if (!(budget.categories?.accommodation || []).length && !templateData?.transport?.accommodations?.length) reminders.push('Accommodation');
    return reminders;
  }, [budget.categories, templateData]);

  return (
    <div data-testid="budget-tracker" className="space-y-4">
      <div>
        <h3 className="font-sans font-semibold text-stone-800 text-sm">Budget</h3>
        <p className="text-xs font-sans text-stone-500 mt-0.5">Track all costs</p>
        <p className="text-[10px] font-sans text-stone-400 mt-1 leading-relaxed">
          Set the total budget, add items in categories below, and track payment deadlines. Costs from other modules (children, transport, accommodation) are collected automatically.
        </p>
      </div>

      {/* Total Budget Input */}
      <div className="bg-white rounded-2xl border border-stone-100 p-4 space-y-3">
        <div>
          <label className="text-xs font-sans font-medium text-stone-600 mb-1.5 block">Total wedding budget (kr)</label>
          <input type="number" value={budget.totalBudget || ''} onChange={e => onChange({ ...budget, totalBudget: e.target.value })} placeholder="e.g. 350000"
            className="w-full md:w-64 text-lg font-sans font-bold px-3 py-2 border border-stone-200 rounded-xl bg-stone-50 focus:border-primary focus:outline-none" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[9px] font-sans font-semibold text-stone-500 uppercase mb-0.5 block">Buffer / Uforutsette (%)</label>
            <input type="number" value={budget.bufferPercent || ''} onChange={e => onChange({ ...budget, bufferPercent: e.target.value })}
              placeholder="e.g. 10" min={0} max={50}
              className="w-full md:w-32 text-xs font-sans px-2.5 py-1.5 border border-stone-200 rounded-lg bg-white focus:border-primary focus:outline-none" />
          </div>
          <div>
            <label className="text-[9px] font-sans font-semibold text-stone-500 uppercase mb-0.5 block">= Buffer (kr)</label>
            <p className="text-xs font-sans font-bold text-stone-600 py-1.5">{bufferAmount.toLocaleString('en-US')} kr</p>
          </div>
        </div>
      </div>

      {/* Master Dashboard */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <DashStat label="Total budget" value={`${totalBudget.toLocaleString('en-US')} kr`} />
        <DashStat label="Estimated total" value={`${masterTotals.estimated.toLocaleString('en-US')} kr`}
          alert={overBudget} />
        <DashStat label="Paid so far" value={`${masterTotals.paid.toLocaleString('en-US')} kr`} color="text-green-600" />
        <DashStat label="Remaining" value={`${masterTotals.remaining.toLocaleString('en-US')} kr`}
          color={masterTotals.remaining > 0 ? 'text-amber-600' : 'text-green-600'} />
        {guestCount > 0 && <DashStat label="Per guest" value={`${masterTotals.estimated > 0 ? Math.round(masterTotals.estimated / guestCount).toLocaleString('en-US') : 0} kr`} />}
      </div>

      {overBudget && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2 text-xs font-sans text-red-700">
          <AlertTriangle className="w-4 h-4" />
          Estimated costs exceed the budget (incl. buffer) by {(masterTotals.estimated - (totalBudget - bufferAmount)).toLocaleString('en-US')} kr
        </div>
      )}

      {/* Upcoming deadlines */}
      {masterTotals.upcomingDeadlines.length > 0 && (
        <div className="bg-amber-50 rounded-xl border border-amber-200/50 p-3 space-y-1">
          <p className="text-[10px] font-sans font-bold text-amber-700 uppercase">Upcoming payment deadlines (next 14 days)</p>
          {masterTotals.upcomingDeadlines.map((dl, i) => (
            <p key={i} className="text-xs font-sans text-amber-600">
              • {dl.name}: {new Date(dl.deadline).toLocaleDateString('en-US')} — {dl.amount.toLocaleString('en-US')} kr remaining
            </p>
          ))}
        </div>
      )}

      {/* Cross-module costs */}
      {crossModuleeCosts.length > 0 && (
        <div className="bg-blue-50 rounded-xl border border-blue-200/50 p-3 space-y-1">
          <p className="text-[10px] font-sans font-bold text-blue-700 uppercase">Kostnader fra andre modules</p>
          {crossModuleeCosts.map((c, i) => (
            <p key={i} className="text-xs font-sans text-blue-600">• {c.label}: {c.amount.toLocaleString('en-US')} kr</p>
          ))}
        </div>
      )}

      {/* Hidden cost reminders */}
      {hiddenCosts.length > 0 && (
        <div className="bg-stone-50 rounded-xl border border-stone-200 p-3 space-y-1">
          <p className="text-[10px] font-sans font-bold text-stone-500 uppercase">Kostnader du kanskje has glemt</p>
          {hiddenCosts.map((c, i) => (
            <p key={i} className="text-xs font-sans text-stone-500">• {c}</p>
          ))}
        </div>
      )}

      {/* Categories */}
      <div className="space-y-2">
        {weddingBudgetCategories.map(cat => (
          <BudgetCategory key={cat.id} category={cat}
            items={budget.categories?.[cat.id] || []}
            onUpdateItems={items => updateCategory(cat.id, items)}
            expanded={expandedCat === cat.id}
            onToggle={() => setExpandedCat(expandedCat === cat.id ? null : cat.id)}
            guestCount={guestCount} />
        ))}
      </div>
    </div>
  );
};

const DashStat = ({ label, value, color = 'text-stone-700', alert = false }) => (
  <div className={`rounded-xl border p-3 text-center ${alert ? 'border-red-200 bg-red-50' : 'border-stone-100 bg-white'}`}>
    <p className={`text-sm font-sans font-bold ${alert ? 'text-red-600' : color}`}>{value}</p>
    <p className="text-[10px] font-sans text-stone-400 uppercase tracking-wide">{label}</p>
  </div>
);
